import React, { Fragment, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import { get, groupBy } from 'lodash';
import Label from 'reactstrap/lib/Label';
import Input from 'reactstrap/lib/Input';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildAllPhotosPath, PhotoflipperModel } from 'client/data/models/photoflipper';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { MpgEntities } from 'client/data/models/mpg';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { venomHistory } from 'client/utils/history/venom-history';
import { MpgParams } from 'site-modules/shared/utils/mpg/params';
import { URL_PATH } from 'site-modules/shared/constants/mpg';
import { DEFAULT_PERSONALIZATION_PARAMS } from 'site-modules/shared/constants/range-and-cost';
import { getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { StyledSelect } from 'site-modules/shared/components/styled-select/styled-select';
import { RangeSlider } from 'site-modules/shared/components/range-slider/range-slider';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { VehiclePhoto } from 'site-modules/shared/components/vehicle-photo/vehicle-photo';
import { SeoStylesMpg } from 'site-modules/shared/components/mpg/seo-styles-mpg/seo-styles-mpg';
import { MpgCards } from 'site-modules/shared/components/mpg/mpg-cards/mpg-cards';

import './fuel-economy-data.scss';

const ZIP_CODE_REGEX = /^\d{5}$/;
const MILES_REGEX = /^\d+$/;
const ENTER_KEY_CODE = 13;
export const VALUE_RANGES = {
  DAILY_COMMUTE: {
    MIN: 0,
    MAX: 2000,
  },
  MILES_PER_YEAR: {
    MIN: 365,
    MAX: 999999,
  },
};

const LABEL_CLASS_NAME = 'mb-0_25 label text-gray-dark d-block';
const ERROR_CLASS_NAME = 'value-error-text text-danger small';
const INPUT_CLASS_NAME = 'size-16 px-1';

export function FuelEconomyDataUI({
  styles,
  style,
  params,
  vehicle,
  photoCount,
  visitorLocation,
  className,
  setModelValue,
  isUsed,
  isMobile,
  titleTag,
  isPreview,
}) {
  const [showZipError, setShowZipCodeError] = useState(false);
  const [milesPerYear, setMilesPerYear] = useState(DEFAULT_PERSONALIZATION_PARAMS.milesPerYear);
  const [dailyCommute, setDailyCommute] = useState(DEFAULT_PERSONALIZATION_PARAMS.dailyCommute);
  const [cityPercents, setCityPercents] = useState(DEFAULT_PERSONALIZATION_PARAMS.cityPercents);
  // used to avoid extra API calls during slider engagement
  const [cityPercentsAPI, setCityPercentsAPI] = useState(DEFAULT_PERSONALIZATION_PARAMS.cityPercents);
  const zipInput = useRef();
  const milesInput = useRef();
  const dailyCommuteInput = useRef();
  const { zipCode } = visitorLocation;

  const onChangeZip = useCallback(() => {
    const zip = zipInput.current.value;

    if (zip === zipCode) {
      setShowZipCodeError(false);
      return;
    }

    if (!ZIP_CODE_REGEX.test(zip)) {
      setShowZipCodeError(true);
      return;
    }

    setModelValue('location', VisitorModel, { zipCode: zip });
    setShowZipCodeError(false);
  }, [setModelValue, zipCode]);

  const onZipKeyDown = useCallback(
    ({ keyCode }) => {
      if (keyCode !== ENTER_KEY_CODE) {
        return;
      }

      onChangeZip();
    },
    [onChangeZip]
  );

  const checkMilesPerYear = () => {
    const currentDailyCommute = get(dailyCommuteInput, 'current.value', 0);
    const currentMilesPerYear = milesInput.current.value;

    const milesPerYearValue = Math.max(currentDailyCommute * 365, currentMilesPerYear);
    milesInput.current.value = milesPerYearValue;

    setMilesPerYear(milesPerYearValue);
  };

  const onChangeMiles = useCallback(() => {
    let newMilesPerYear = milesInput.current.value;

    newMilesPerYear = MILES_REGEX.test(newMilesPerYear) ? +newMilesPerYear : VALUE_RANGES.MILES_PER_YEAR.MIN;
    newMilesPerYear = Math.min(
      Math.max(newMilesPerYear, VALUE_RANGES.MILES_PER_YEAR.MIN),
      VALUE_RANGES.MILES_PER_YEAR.MAX
    );

    milesInput.current.value = newMilesPerYear;

    checkMilesPerYear();
  }, []);

  const onMilesKeyDown = useCallback(
    ({ keyCode }) => {
      if (keyCode !== ENTER_KEY_CODE) {
        return;
      }

      onChangeMiles();
    },
    [onChangeMiles]
  );

  const onChangeDailyCommute = useCallback(() => {
    let newDailyCommute = dailyCommuteInput.current.value;

    newDailyCommute = MILES_REGEX.test(newDailyCommute) ? +newDailyCommute : VALUE_RANGES.DAILY_COMMUTE.MIN;
    newDailyCommute = Math.min(
      Math.max(newDailyCommute, VALUE_RANGES.DAILY_COMMUTE.MIN),
      VALUE_RANGES.DAILY_COMMUTE.MAX
    );

    dailyCommuteInput.current.value = newDailyCommute;

    checkMilesPerYear();

    setDailyCommute(newDailyCommute);
  }, []);

  const onDailyCommuteKeyDown = useCallback(
    ({ keyCode }) => {
      if (keyCode !== ENTER_KEY_CODE) {
        return;
      }

      onChangeDailyCommute();
    },
    [onChangeDailyCommute]
  );

  const setCityPercentsAPIDebounced = debounce(percentsValue => setCityPercentsAPI(percentsValue), 250);

  const onSliderChange = useCallback(({ target: { value: percentsValue } }) => {
    setCityPercents(percentsValue);
    setCityPercentsAPIDebounced(percentsValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeStyle = useCallback(
    ({ id }) => {
      const selectedStyle = styles.find(({ id: styleId }) => styleId === id);
      const styleName = get(selectedStyle, 'name', '');
      const submodelName = get(selectedStyle, 'submodelName', '');

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_TOOL_SELECT,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          subaction_name: TrackingConstant.MPG_MODEL_SELECT,
          value: `${submodelName} ${styleName}`,
        },
      });

      venomHistory.push(`${getUrlPathFromParams(params, URL_PATH)}?style=${id}`);
    },
    [params, styles]
  );

  const {
    make: { name: makeName },
    model: { name: modelName },
    year,
  } = vehicle;
  const vehicleName = `${year} ${makeName} ${modelName}`;

  const isPHEV = style.engineType === 'plug-in hybrid' || (style.engineType === 'hybrid' && !!style.evData.range);

  const drivingHabitsRange = (
    <Fragment>
      <Label for="mpg-range-slider" className={LABEL_CLASS_NAME}>
        Your Driving Habits
      </Label>
      <RangeSlider
        min={0}
        max={100}
        step={5}
        className="w-100 mb-1"
        defaultValue={cityPercents}
        aria-valuetext={`${cityPercents}% city and ${100 - cityPercents}% highway`}
        onChange={onSliderChange}
        data-tracking-id="mpg_change_percents"
        id="mpg-range-slider"
      />
      <div className="d-flex justify-content-between medium text-gray-darker mb-1_5 mb-md-0">
        <div className="text-start">
          <div className="heading-5">{cityPercents}%</div>
          <div>City</div>
        </div>
        <div className="text-end">
          <div className="heading-5">{100 - cityPercents}%</div>
          <div>Highway</div>
        </div>
      </div>
    </Fragment>
  );

  const personalizeCosts = (
    <Fragment>
      <Row>
        <Col xs={6} className={classnames({ 'has-danger': showZipError })}>
          <Label for="mpg-zip-input" className={LABEL_CLASS_NAME}>
            Zip Code
          </Label>
          <Input
            name="zip"
            inputMode="numeric"
            pattern="[0-9]*"
            bsSize="sm"
            className={INPUT_CLASS_NAME}
            defaultValue={zipCode}
            innerRef={zipInput}
            onKeyDown={onZipKeyDown}
            onBlur={onChangeZip}
            data-tracking-id="mpg_change_zip"
            id="mpg-zip-input"
          />
          <div className={classnames(ERROR_CLASS_NAME, { invisible: !showZipError })} aria-hidden={!showZipError}>
            Please enter a valid zip code.
          </div>
        </Col>
        <Col xs={6}>
          <Label for="mpg-mile-year-input" className={LABEL_CLASS_NAME}>
            Miles per Year
          </Label>
          <Input
            name="mile-year"
            type="number"
            min={365}
            max={999999}
            bsSize="sm"
            className={INPUT_CLASS_NAME}
            defaultValue={milesPerYear}
            innerRef={milesInput}
            onKeyDown={onMilesKeyDown}
            onBlur={onChangeMiles}
            data-tracking-id="mpg_change_miles"
            id="mpg-mile-year-input"
          />
        </Col>
        {isPHEV && (
          <Col xs={6} className="mb-1 mb-md-0">
            <Label for="mpg-daily-commute-input" className={LABEL_CLASS_NAME}>
              Daily Commute
            </Label>
            <Input
              name="daily-commute"
              type="number"
              min={0}
              max={2000}
              bsSize="sm"
              className={INPUT_CLASS_NAME}
              defaultValue={dailyCommute}
              innerRef={dailyCommuteInput}
              onKeyDown={onDailyCommuteKeyDown}
              onBlur={onChangeDailyCommute}
              data-tracking-id="mpg_change_daily_commute"
              id="mpg-daily-commute-input"
            />
          </Col>
        )}
      </Row>
      {isMobile && drivingHabitsRange}
    </Fragment>
  );

  return (
    <div className={classnames('fuel-economy-data', className)}>
      <Row className="mb-md-1_5">
        <Col xs={12} md={4} className="position-relative">
          <AspectRatioContainer className="vehicle-photo-wrapper" aspectRatio="2:1">
            <VehiclePhoto params={params} vehicle={vehicle} altText={vehicleName} />
          </AspectRatioContainer>
          <div className="photos-count text-gray-dark fw-medium small pos-a top-0 right-0 mt-0_5 me-0_75 px-1 py-0_5 rounded">
            <i className="icon-camera" aria-hidden /> 1 of {photoCount}
          </div>
        </Col>
        <Col xs={12} md={4}>
          {isMobile && <hr className="mt-1 mb-1_25" />}
          {!isPreview && (
            <StyledSelect
              value={style.id}
              title={style.name}
              defaultValue={get(styles, '[0].id')}
              labelText="Select Model"
              labelClassName={LABEL_CLASS_NAME}
              optionsGrouped={groupBy(styles, 'submodelName')}
              onChange={changeStyle}
              name="select-style"
              valueKey="id"
              labelKey="name"
              className={classnames(INPUT_CLASS_NAME, 'form-control mb-0_75 mb-md-1_25')}
              id="mpg-summary-select-model"
            />
          )}
          {isMobile ? (
            <Fragment>
              <ContentCollapse
                textClosed="Personalize your driving habits"
                textOpen="Personalize your driving habits"
                justify="justify-content-start"
                btnContainerClasses="m-0 p-0"
                btnClasses="ps-0 pe-0_25 py-0_75 size-16 text-primary-darker w-100 d-flex justify-content-between align-items-center"
                btnTextClasses="fw-normal"
                btnArrowClasses="medium"
                btnArrowIconClasses={{ up: 'icon-arrow-up3', down: 'icon-arrow-down3' }}
                showBtnBelow
                isEclipseFade={false}
                btnTrackingId="view_content"
                isSameExpandCollapseTracking
                btnIgnoreTrackingValue={false}
              >
                {personalizeCosts}
              </ContentCollapse>
              <hr className="mt-0 mb-1_25" />
            </Fragment>
          ) : (
            personalizeCosts
          )}
        </Col>
        {!isMobile && (
          <Col xs={12} md={4}>
            {drivingHabitsRange}
          </Col>
        )}
      </Row>
      {!isPreview && <SeoStylesMpg styles={styles} vehicleName={vehicleName} />}
      <MpgCards
        style={style}
        params={params}
        styleParams={{
          year,
          makeName,
          modelName,
          name: style.name,
          trimName: style.trimName,
        }}
        isElectric={style.engineType === 'electric'}
        isPHEV={isPHEV}
        personalizationParams={{
          milesPerYear,
          cityPercents: cityPercentsAPI,
          dailyCommute: isPHEV ? dailyCommute : undefined,
        }}
        visitorLocation={visitorLocation}
        isUsed={isUsed}
        titleTag={titleTag}
        isPreview={isPreview}
        isMobile={isMobile}
      />
    </div>
  );
}

FuelEconomyDataUI.propTypes = {
  params: MpgParams.isRequired,
  styles: PropTypes.arrayOf(MpgEntities.StyleMpg).isRequired,
  style: MpgEntities.StyleMpg.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  setModelValue: PropTypes.func.isRequired,
  className: PropTypes.string,
  photoCount: PropTypes.number,
  visitorLocation: VisitorEntities.Location,
  isUsed: PropTypes.bool,
  isMobile: PropTypes.bool,
  titleTag: PropTypes.string,
  isPreview: PropTypes.bool,
};

FuelEconomyDataUI.defaultProps = {
  className: null,
  photoCount: 1,
  visitorLocation: null,
  isUsed: false,
  isMobile: false,
  titleTag: undefined,
  isPreview: false,
};

export const FuelEconomyData = connectToModel(FuelEconomyDataUI, {
  visitorLocation: bindToPath('location', VisitorModel),
  photoCount: bindToPath(
    ({ params, vehicle }) =>
      buildAllPhotosPath({
        ...params,
        pageNum: 1,
        submodel: vehicle.submodels.name,
        imageSize: 600,
      }),
    PhotoflipperModel,
    ({ totalCount }) => totalCount
  ),
});
